import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (newLanguage: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const params = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize language from localStorage or default to 'fr'
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    // The URL wins (so a pre-rendered /en page hydrates in English), then the
    // saved preference, then French.
    const fromUrl =
      typeof window !== "undefined"
        ? window.location.pathname.match(/^\/(fr|en)(?=\/|$)/)?.[1]
        : undefined;
    if (fromUrl) return fromUrl;
    const savedLanguage = localStorage.getItem("preferred-language");
    return savedLanguage === "fr" || savedLanguage === "en"
      ? savedLanguage
      : "fr";
  });

  // Update current language when the URL changes. The provider sits above
  // <Routes>, so useParams() is empty here: read the prefix from the pathname.
  useEffect(() => {
    const match = location.pathname.match(/^\/(fr|en)(?=\/|$)/);
    const urlLang = match ? match[1] : params.lang;
    if (urlLang && (urlLang === "fr" || urlLang === "en")) {
      if (urlLang !== currentLanguage) {
        setCurrentLanguage(urlLang);
        localStorage.setItem("preferred-language", urlLang);
      }
    }
  }, [params.lang, location.pathname, currentLanguage]);

  const changeLanguage = (newLanguage: string): void => {
    if (newLanguage === currentLanguage) return;

    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(fr|en)/, "") || "";
    const newPath = `/${newLanguage}${pathWithoutLang}`;


    // Update localStorage
    localStorage.setItem("preferred-language", newLanguage);

    // Update state
    setCurrentLanguage(newLanguage);

    // Navigate to new language path (client-side navigation)
    navigate(newPath, { replace: true });
  };

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
